import { useFpB2bDeliveryClassList } from '@cart-services/hooks/fp-b2b/use-fp-b2b-delivery-class-list'
import { useFpB2bShippingService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-shipping-service'
import { CountryCode } from '@constants'
import { countryCodeMap } from '@constants'
import { type CheckoutOrder } from '@hooks-api/types/dataModel/orders'
import { DELIVERY_CLASS } from '@hooks-api/types/dataModel/shipping'
import { type Nullable } from '@hooks-api/types/nullable'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { DialogContent } from '@pets-ui/DialogContent/DialogContent'
import { DialogHeader } from '@pets-ui/DialogHeader/DialogHeader'
import { HStack } from '@pets-ui/HStack/HStack'
import { Radio } from '@pets-ui/Radio/Radio'
import { ResponsiveDialog } from '@pets-ui/ResponsiveDialog/ResponsiveDialog'
import { Select } from '@pets-ui/Select/Select'
import { SelectOption } from '@pets-ui/SelectOption/SelectOption'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { formatPrice } from '@utils/formatCurrency'
import { ShippingListItemRule } from '@wp-site-wide/components/ShippingListItemRule/ShippingListItemRule'
import { useEffect, useMemo, useRef } from 'react'
import type { ReactNode } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useCheckout } from '../../../../fp-b2b-shared/hooks/use-checkout'
import { formatShippingPromotionDescription } from '../../../../fp-b2b-shared/utils/format-shipping-promote-description'
import { AddressReceiver } from '../AddressReceiver/AddressReceiver'

const footer = stylex.create({
  root: {
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  button: {
    backgroundColor: colors.black,
    color: colors.white,
  },
})

const Footer = ({ onSubmit }: { onSubmit: ShippingMethodDialogProps['onSubmit'] }) => {
  const isMobile = useResponsive('mobile')
  return (
    <Box rootStyle={footer.root}>
      <Button variant="filled" rootStyle={footer.button} onClick={onSubmit} fullWidth={isMobile}>
        確認
      </Button>
    </Box>
  )
}

const DialogShippingMethodContent = () => {
  const { campaignCode, campaignId, currency, platform } = useCheckout()
  const { setValue, control } = useFormContext<CheckoutOrder>()
  const deliveryClassList = useFpB2bDeliveryClassList(campaignId, platform)
  const { shippingMethods, appliedShippingMethodId, setAppliedShippingMethodId, appliedShippingMethod, getAppliedShippingInfo } = useFpB2bShippingService(campaignId, campaignCode, platform)
  const billingCountry = useWatch({ control, name: 'billing_country' })
  const deliveryClass = deliveryClassList[0] || DELIVERY_CLASS.NORMAL
  const rules = useMemo(() => {
    return appliedShippingMethod?.shipping_rules?.[deliveryClass]
  }, [appliedShippingMethod, deliveryClass])

  const { minShippingAmount, minShippingFee } = useMemo(() => {
    return getAppliedShippingInfo(deliveryClass)
  }, [deliveryClass, getAppliedShippingInfo])

  const promoteDescription = useMemo(() => {
    if (!minShippingAmount || !minShippingFee) return null
    return formatShippingPromotionDescription(minShippingAmount, minShippingFee, currency)
  }, [minShippingAmount, minShippingFee, currency])

  const getShippingRuleDescription = (fee: number): ReactNode => {
    return (
      <>
        運費
        {' '}
        {formatPrice(currency, fee)}
        {promoteDescription ? `；${promoteDescription}` : promoteDescription}
      </>
    )
  }

  const handleChangeShippingMethod = (value: Nullable<number>) => {
    if (!value) {
      console.error('[DialogShippingMethod] shipping_method_id is null')
      return
    }
    setAppliedShippingMethodId(value)
  }

  useEffect(() => {
    if (!appliedShippingMethodId) return
    setAppliedShippingMethodId(appliedShippingMethodId)
  }, [appliedShippingMethodId, setAppliedShippingMethodId])

  const availableBillingCountries = [CountryCode.TW, CountryCode.HK, CountryCode.MO]

  const handleChangeBillingCountry = (country: CountryCode) => {
    setValue('billing_country', country)
  }

  return (
    <DialogContent pb={0}>
      <VStack gap={3}>
        <HStack height={32} justifyContent="space-between" alignItems="center">
          <Text variant="subtitle">
            配送方式
            ：
          </Text>
          {availableBillingCountries.length > 1 && (
            <HStack gap={3} alignItems="center">
              <Text fontWeight={500}>地區</Text>
              <Select
                value={billingCountry}
                onChange={value => handleChangeBillingCountry(value as CountryCode)}
                disabled
                placeholder="選擇地區"
              >
                {availableBillingCountries.map(country => (
                  <SelectOption key={country} value={country}>
                    {countryCodeMap[country]}
                  </SelectOption>
                ))}
              </Select>
            </HStack>
          )}
        </HStack>
        <Text variant="caption" color={colors.grey600}>
          *
          僅顯示常態運費規則（不包含運費優惠）
        </Text>
        {shippingMethods.map(method => (
          <VStack
            key={method.shipping_method_id}
            gap={2}
            p={3}
            alignItems="flex-start"
            borderStyle="solid"
            borderColor={colors.grey300}
            borderWidth={1}
            borderRadius={8}
            width="100%"
          >
            <Radio
              color="blue"
              label={method.name}
              value={method.shipping_method_id}
              onChange={() => handleChangeShippingMethod(method.shipping_method_id)}
              checked={appliedShippingMethodId === method.shipping_method_id}
            />
            {rules?.map(rule => (
              <ShippingListItemRule
                key={rule.shipping_rule_id}
                description={getShippingRuleDescription(rule.applied_shipping_fee.fee)}
                deliveryClass={rule.delivery_class}
              />
            ))}
            {appliedShippingMethodId === method.shipping_method_id && (
              <Box p={3} backgroundColor={colors.grey50} borderRadius={8} width="100%">
                <AddressReceiver />
              </Box>
            )}
          </VStack>
        ))}
      </VStack>
    </DialogContent>
  )
}

interface ShippingMethodDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: () => void
}

interface ShippingMethodObject {
  billingAddressState: CheckoutOrder['billing_address_state']
  billingAddressCity: CheckoutOrder['billing_address_city']
  billingAddressPostcode: CheckoutOrder['billing_address_postcode']
  billingAddressAddress1: CheckoutOrder['billing_address_address1']
}

export function DialogShippingMethod({ open, onClose, onSubmit }: ShippingMethodDialogProps) {
  const { watch, setValue } = useFormContext<CheckoutOrder>()
  const formValues = watch()
  const shippingMethodObjectRef = useRef<ShippingMethodObject>({
    billingAddressState: formValues?.billing_address_state,
    billingAddressCity: formValues?.billing_address_city,
    billingAddressPostcode: formValues?.billing_address_postcode,
    billingAddressAddress1: formValues?.billing_address_address1,
  })

  useEffect(() => {
    if (open) return
    shippingMethodObjectRef.current = {
      billingAddressState: formValues?.billing_address_state,
      billingAddressCity: formValues?.billing_address_city,
      billingAddressPostcode: formValues?.billing_address_postcode,
      billingAddressAddress1: formValues?.billing_address_address1,
    }
  }, [formValues, open])

  const handleReset = () => {
    setValue('billing_address_state', shippingMethodObjectRef.current.billingAddressState)
    setValue('billing_address_city', shippingMethodObjectRef.current.billingAddressCity)
    setValue('billing_address_postcode', shippingMethodObjectRef.current.billingAddressPostcode)
    setValue('billing_address_address1', shippingMethodObjectRef.current.billingAddressAddress1)
  }

  const handleClose = () => {
    handleReset()
    onClose()
  }

  return (
    <ResponsiveDialog open={open} fullWidth>
      <DialogHeader title="配送方式" onClose={handleClose} />
      <DialogShippingMethodContent />
      <Footer onSubmit={onSubmit} />
    </ResponsiveDialog>
  )
}
